import React, { useState } from "react";
import PropTypes from "prop-types";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Fab from "@mui/material/Fab";
import CircularProgress from "@mui/material/CircularProgress";
import { useApi } from "../../../../../context/api.js";
import MDButton from "../../../../../components/MDButton";
import MDTypography from "../../../../../components/MDTypography";
import MDBox from "../../../../../components/MDBox";
import AddCircleIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import DocumentIcon from "@mui/icons-material/Description";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import QRCodeScanner from "../../../../../components/QRCodeScanner";
import MDProgress from "../../../../../components/MDProgress";
import UploadIcon from "../../../../../assets/icons/upload-icon.js";
import { setMilestone, useHomework } from "../../../../../context/homework";

function ManualValidation({ m, title, proofs = false, min = 0, max = 5, onCancel }) {
  const api = useApi();
  const [_, dispatch] = useHomework();
  const [files, setFiles] = useState([]);
  const [showUploader, setShowUploader] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFileUpload = (file) => {
    const id = "file-" + Math.round(Math.random() * 100000 + 100000);
    setShowUploader(false);
    setFiles((files) =>
      files.concat({ id, name: file.name, type: file.type, uploading: true, progress: 0 })
    );
    api
      .uploadFile(file, "temp", file.type, file.name, (current, total) =>
        setFiles((files) =>
          files.map((f) => (f.id === id ? { ...f, progress: (current * 100) / total } : f))
        )
      )
      .then((res) => {
        if (res.success) {
          setFiles((files) =>
            files.map((f) =>
              f.id === id ? { ...f, id: res.id, uploading: false, success: true } : f
            )
          );
        } else {
          setFiles((files) =>
            files.map((f) => (f.id === id ? { ...f, uploading: false, error: true } : f))
          );
        }
      });
  };

  const handleCancelFileUpload = () => {
    setShowUploader(false);
  };

  const handleRemoveFile = (f) => {
    setFiles((files) => files.filter((file) => file.id !== f.id));
  };

  const handleSubmission = () => {
    if (files.length >= min && !files.find((f) => f.uploading)) {
      setLoading(true);
      api
        .submitManualValidation(
          m.id,
          files.map((f) => f.id)
        )
        .then((res) => {
          setLoading(false);
          if (res) {
            setMilestone(dispatch, m.id);
            onCancel();
          } else {
            // TODO : Show error ?
          }
        })
        .catch((err) => {
          setLoading(false);
          // TODO : Show error ?
        });
    }
  };

  return (
    <>
      <DialogContent>
        {title}
        <MDTypography variant="body" component="div" mt={2}>
          Tu es sur le point de notifier ton parent que tu as terminé cette étape.
        </MDTypography>
        {proofs && (
          <>
            <MDTypography variant="body" component="div" mt={0.5}>
              Tu peux joindre des preuves de ton travail pour que ton parent puisse le valider
              correctement.
            </MDTypography>
            {min > 0 && (
              <MDTypography sx={{ color: "red", fontStyle: "italic", fontSize: 12 }}>
                Au minimum {min} preuve{min > 1 ? "s" : ""} {min > 1 ? "sont" : "est"} requise
                {min > 1 ? "s" : ""} pour valider cette étape.
              </MDTypography>
            )}
            {showUploader && (
              <QRCodeScanner
                mode="file"
                onResult={handleFileUpload}
                onCancel={handleCancelFileUpload}
              />
            )}
            {!showUploader && (
              <div style={{ position: "relative", paddingBottom: "75%" }}>
                <MDBox
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    border: "1px solid silver",
                    borderRadius: 8,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    flexWrap: "wrap",
                    padding: 8,
                  }}
                >
                  {files.map((f) => (
                    <MDBox
                      key={f.id}
                      style={{
                        border: "1px solid silver",
                        padding: 16,
                        borderRadius: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                        minWidth: 120,
                        maxWidth: 240,
                        margin: 8,
                      }}
                    >
                      {f.uploading && (
                        <UploadIcon sx={{ width: 64, height: 64, mb: 1 }} value={f.progress} />
                      )}
                      {!f.uploading && f.type.startsWith("image/") && (
                        <MDBox
                          component="span"
                          sx={{
                            height: 64,
                            width: "100%",
                            position: "relative",
                            mb: 1,
                          }}
                        >
                          <img
                            src={"/files/temp/" + f.id}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              margin: "auto",
                              maxWidth: "100%",
                              maxHeight: "100%",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          />
                        </MDBox>
                      )}
                      {!f.uploading && f.type.startsWith("audio/") && (
                        <AudioFileIcon sx={{ width: 64, height: 64, mb: 1 }} />
                      )}
                      {!f.uploading && f.type.startsWith("video/") && (
                        <VideoFileIcon sx={{ width: 64, height: 64, mb: 1 }} />
                      )}
                      {!f.uploading &&
                        !f.type.startsWith("image/") &&
                        !f.type.startsWith("audio/") &&
                        !f.type.startsWith("video/") && (
                          <DocumentIcon sx={{ width: 64, height: 64, mb: 1 }} />
                        )}
                      <Fab
                        size="small"
                        color="error"
                        sx={{ position: "absolute", top: -12, right: -12 }}
                        onClick={() => handleRemoveFile(f)}
                      >
                        <DeleteIcon sx={{ height: "24px", width: "24px" }} />
                      </Fab>
                      <MDTypography
                        component="div"
                        variant="caption"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                        }}
                      >
                        {f.name}
                      </MDTypography>
                      {f.uploading && (
                        <MDProgress
                          variant="gradient"
                          value={f.progress || 0}
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 0,
                            },
                          }}
                          color="info"
                        />
                      )}
                    </MDBox>
                  ))}
                  {files.length < max && (
                    <MDBox
                      style={{
                        border: "1px solid silver",
                        padding: 16,
                        borderRadius: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                        position: "relative",
                        minWidth: 120,
                        margin: 8,
                      }}
                      onClick={() => setShowUploader(true)}
                    >
                      <AddCircleIcon sx={{ width: 64, height: 64, mb: 1 }} />
                      <MDTypography component="div" variant="caption">
                        Ajouter une preuve
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton disabled={loading || !m} onClick={onCancel}>
          Annuler
        </MDButton>
        <MDButton
          variant="contained"
          color="info"
          disabled={loading || files.length < min || !!files.find((f) => f.uploading)}
          onClick={handleSubmission}
        >
          {loading && <CircularProgress size={24} />}
          {!loading && "Envoyer"}
        </MDButton>
      </DialogActions>
    </>
  );
}

ManualValidation.propTypes = {
  m: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.element,
  proofs: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
};

export default ManualValidation;
